import { type CSSProperties, type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader, ErrorState, StatusPill } from '@ui-kit';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTcpStatus } from '../hooks/useTcpStatus';
import { useTcpEcho } from '../hooks/useTcpEcho';
import { IntegrationCommonUsagePanel } from '../components/IntegrationCommonUsagePanel';
import { HexFrameView } from '../components/HexFrameView';
import { DurationTimeline } from '../components/CallTimeline';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import { tcpEchoFormSchema, type TcpEchoFormValues } from '../schema/integrationHub.schema';
import type { ApiError } from '../types/integrationHub';

/**
 * TcpConsolePage — TCP echo 콘솔 (/integration-hub/tcp · §Δ.3)
 * 5상태: loading(상태 조회 skeleton + 전송 spinner) / empty(전송 전) / error(INTGHUB-502-001/503-001) / default(응답+hex+타임라인) / dialog(N/A — 인라인 콘솔)
 */
export function TcpConsolePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const statusQuery = useTcpStatus();
  const echoMutation = useTcpEcho();

  const [echoError, setEchoError] = useState<ApiError | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<TcpEchoFormValues>({
    resolver: zodResolver(tcpEchoFormSchema),
    defaultValues: { text: '' },
  });

  const onSubmit = useCallback(
    (values: TcpEchoFormValues) => {
      setEchoError(null);
      echoMutation.mutate(
        { text: values.text },
        { onError: (err) => setEchoError(err as ApiError) }
      );
    },
    [echoMutation]
  );

  const status = statusQuery.data;
  const isStatusLoading = statusQuery.isLoading;
  const isStatusError = statusQuery.isError;
  const is403 = isStatusError && (statusQuery.error as unknown as ApiError)?.code === 'AUTH-403-001';

  const result = echoMutation.data ?? null;
  const isEchoPending = echoMutation.isPending;

  return (
    <div style={{ padding: 0 }}>
      <PageHeader
        title={t('integrationHub.tcp.title')}
        breadcrumb={<span>{t('integrationHub.tcp.breadcrumb')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => navigate('/integration-hub')} style={tabButtonStyle}>
              {t('integrationHub.tab.endpoints')}
            </button>
            <button type="button" onClick={() => navigate('/integration-hub/storage')} style={tabButtonStyle}>
              {t('integrationHub.tab.storage')}
            </button>
            <button type="button" onClick={() => navigate('/integration-hub/history')} style={tabButtonStyle}>
              {t('integrationHub.tab.history')}
            </button>
            <button type="button" onClick={() => navigate('/integration-hub/grpc')} style={tabButtonStyle}>
              {t('integrationHub.tab.grpc')}
            </button>
          </div>
        }
      />

      {/* status loading skeleton */}
      {isStatusLoading && (
        <div aria-label={t('integrationHub.tcp.status.loadingAriaLabel')} aria-busy="true" style={cardStyle}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 16,
                width: `${35 + (i * 15) % 40}%`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
                marginBottom: 'var(--spacing-2)',
              }}
            />
          ))}
        </div>
      )}

      {/* status error */}
      {isStatusError && (
        <div style={cardStyle}>
          {is403 ? (
            <ErrorState
              title={t('integrationHub.error.AUTH-403-001')}
              message={t('integrationHub.error.forbidden')}
              onRetry={() => navigate('/')}
            />
          ) : (
            <ErrorState
              title={t('integrationHub.error.loadFailed')}
              message={(statusQuery.error as unknown as ApiError)?.message ?? t('integrationHub.error.network')}
              onRetry={() => void statusQuery.refetch()}
            />
          )}
        </div>
      )}

      {/* default — status 카드 + echo 콘솔 */}
      {!isStatusLoading && !isStatusError && status && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          <section aria-label={t('integrationHub.tcp.status.title')} style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('integrationHub.tcp.status.title')}</h3>
            <div style={infoGridStyle}>
              <InfoField label={t('integrationHub.tcp.status.running')}>
                <StatusPill tone={status.serverRunning ? 'success' : 'danger'}>
                  {status.serverRunning
                    ? t('integrationHub.tcp.status.running.yes')
                    : t('integrationHub.tcp.status.running.no')}
                </StatusPill>
              </InfoField>
              <InfoField label={t('integrationHub.tcp.status.port')}>
                <code style={monoStyle}>{status.boundPort}</code>
              </InfoField>
              <InfoField label={t('integrationHub.tcp.status.bindAddress')}>
                <code style={monoStyle}>{status.bindAddress}</code>
              </InfoField>
              <InfoField label={t('integrationHub.tcp.status.maxFrame')}>
                <code style={monoStyle}>{status.maxFrameLength}B</code>
              </InfoField>
            </div>
            {!status.enabled && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)' }}>
                {t('integrationHub.error.INTGHUB-503-001')}
              </p>
            )}
          </section>

          {/* echo 입력 */}
          <section aria-label={t('integrationHub.tcp.input.title')} style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('integrationHub.tcp.input.title')}</h3>
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 240px' }}>
                <input
                  type="text"
                  placeholder={t('integrationHub.tcp.input.placeholder')}
                  aria-invalid={!!errors.text}
                  aria-describedby={errors.text ? 'tcp-text-error' : undefined}
                  style={inputStyle}
                  {...register('text')}
                />
                {errors.text && (
                  <span id="tcp-text-error" role="alert" style={errorTextStyle}>
                    {errors.text.message}
                  </span>
                )}
              </div>
              <ButtonGuard requirePermission="INTEGRATION_INVOKE">
                {({ disabled, reason }) => (
                  <button
                    type="submit"
                    disabled={disabled || isEchoPending}
                    title={reason}
                    style={{
                      ...primaryButtonStyle,
                      opacity: disabled || isEchoPending ? 0.6 : 1,
                      cursor: disabled || isEchoPending ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isEchoPending ? '...' : t('integrationHub.tcp.button.send')}
                  </button>
                )}
              </ButtonGuard>
            </form>

            {/* loading — 전송 중 */}
            {isEchoPending && (
              <p aria-live="polite" style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('integrationHub.tcp.status.sending')}
              </p>
            )}

            {/* error */}
            {echoError && !isEchoPending && (
              <div role="alert" style={errorBoxStyle(echoError.code === 'INTGHUB-503-001')}>
                {echoError.code === 'INTGHUB-503-001' ? (
                  <>
                    <strong>{t('integrationHub.error.INTGHUB-503-001')}</strong>
                  </>
                ) : (
                  <>
                    <strong>{t('integrationHub.echoConsole.error.unreachable')}</strong>
                    <button type="button" onClick={() => void statusQuery.refetch()} style={{ ...secondaryButtonStyle, marginLeft: 'var(--spacing-3)' }}>
                      {t('integrationHub.button.retry')}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* empty — 전송 전 */}
            {!result && !echoError && !isEchoPending && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {t('integrationHub.tcp.empty.description')}
              </p>
            )}

            {/* default — 결과 */}
            {result && !isEchoPending && (
              <div style={{ marginTop: 'var(--spacing-4)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                  <StatusPill tone={result.echoMatched ? 'success' : 'danger'}>
                    {result.echoMatched
                      ? t('integrationHub.tcp.result.matched')
                      : t('integrationHub.tcp.result.mismatched')}
                  </StatusPill>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                    {t('integrationHub.tcp.result.response')}: {result.response}
                  </span>
                </div>

                <HexFrameView
                  label={t('integrationHub.tcp.hex.request')}
                  hex={result.framePrefixHex + result.requestHex}
                  prefixHex={result.framePrefixHex}
                />
                <HexFrameView label={t('integrationHub.tcp.hex.response')} hex={result.responseHex} />

                <div>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-2)' }}>
                    {t('integrationHub.tcp.timeline.title')}
                  </p>
                  <DurationTimeline
                    totalMs={result.totalMs}
                    totalLabel={t('integrationHub.tcp.timeline.total')}
                    segments={[
                      { label: t('integrationHub.tcp.timeline.connect'), ms: result.connectMs, color: 'var(--color-info)' },
                      { label: t('integrationHub.tcp.timeline.write'), ms: result.writeMs, color: 'var(--color-brand-primary)' },
                      { label: t('integrationHub.tcp.timeline.read'), ms: result.readMs, color: 'var(--color-success)' },
                    ]}
                  />
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {/* 공통 모듈 패널 */}
      <IntegrationCommonUsagePanel />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

function InfoField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>{label}</p>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>{children}</div>
    </div>
  );
}

function errorBoxStyle(isDisabled: boolean): CSSProperties {
  return {
    marginTop: 'var(--spacing-3)',
    padding: 'var(--spacing-3)',
    border: `1px solid ${isDisabled ? 'var(--color-warning)' : 'var(--color-danger)'}`,
    borderRadius: 'var(--radius-md)',
    background: 'var(--color-surface-muted)',
    fontSize: 'var(--font-size-sm)',
    color: isDisabled ? 'var(--color-warning)' : 'var(--color-danger)',
  };
}

const tabButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const primaryButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const secondaryButtonStyle: CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const cardStyle: CSSProperties = {
  padding: 'var(--spacing-5)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-background)',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-4)',
};

const infoGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 'var(--spacing-4)',
};

const inputStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  width: '100%',
};

const monoStyle: CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-sm)',
};

const errorTextStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--spacing-1)',
};
